import React from 'react';
import { motion } from 'framer-motion';
import { Scissors, Droplets, Sparkles, Crown, Palette, Heart, Flower2, Gem } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const ServicesSection = () => {
  const { t, isRTL } = useLanguage();

  const services = [
    {
      icon: Scissors,
      title: t.services.hairStyling,
      description: t.services.hairStylingDesc,
      image: 'https://images.unsplash.com/photo-1631451497670-c7fc917197cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    },
    {
      icon: Droplets,
      title: t.services.hairWashing,
      description: t.services.hairWashingDesc,
      image: 'https://images.unsplash.com/photo-1599387737838-660b75526801?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    },
    {
      icon: Sparkles,
      title: t.services.hairTreatment,
      description: t.services.hairTreatmentDesc,
      image: 'https://images.unsplash.com/photo-1669058611053-ff24fb0f1c33?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    },
    {
      icon: Crown,
      title: t.services.professionalStyling,
      description: t.services.professionalStylingDesc,
      image: 'https://images.unsplash.com/photo-1633681121751-e4a0392602b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    },
    {
      icon: Palette,
      title: t.services.makeup,
      description: t.services.makeupDesc,
      image: 'https://images.unsplash.com/photo-1739980296455-3f8d6051ca20?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    },
    {
      icon: Heart,
      title: t.services.nails,
      description: t.services.nailsDesc,
      image: 'https://images.unsplash.com/photo-1542848285-4777eb2a621e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    },
    {
      icon: Flower2,
      title: t.services.facial,
      description: t.services.facialDesc,
      image: 'https://images.unsplash.com/photo-1683408640631-2c99fff964d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    },
    {
      icon: Gem,
      title: t.services.spa,
      description: t.services.spaDesc,
      image: 'https://images.unsplash.com/photo-1756483510882-55bc1249642d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="section-padding bg-[#F9F7F2]"
    >
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#D4A39A] text-sm font-semibold uppercase tracking-widest">
            {t.services.subtitle}
          </span>
          <h2 
            data-testid="services-title"
            className="heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#4A4A4A] mt-3"
          >
            {t.services.title}
          </h2>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              data-testid={`service-card-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              className="service-card group cursor-pointer"
            >
              {/* Image */}
              <div className="relative h-40 rounded-lg overflow-hidden mb-4">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              
              {/* Icon */}
              <div className="w-12 h-12 rounded-full bg-[#F9F7F2] flex items-center justify-center mb-4 group-hover:bg-[#D4A39A] transition-colors">
                <service.icon className="w-6 h-6 text-[#D4A39A] group-hover:text-white transition-colors" />
              </div>
              
              {/* Content */}
              <h3 className="heading text-lg font-semibold text-[#4A4A4A] mb-2">
                {service.title}
              </h3>
              <p className="text-sm text-[#7D7D7D] leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
